import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import CustomerHeader from '../../components/ui/CustomerHeader';
import Footer from '../../components/ui/Footer';
import AuthenticationModal from '../../components/ui/AuthenticationModal';
import CartSidebar from '../../components/ui/CartSidebar';
import FilterSidebar from './components/FilterSidebar';
import SearchBar from './components/SearchBar';
import SortAndViewControls from './components/SortAndViewControls';
import ProductGrid from './components/ProductGrid';
import Button from '../../components/ui/Button';
import Pagination from './components/Pagination';

const ProductCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { addItem, totalCount } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCartSidebar, setShowCartSidebar] = useState(false);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);
  // cart count comes from CartContext; legacy state kept as alias for unchanged callers below.
  const cartItemCount = totalCount;
  const [isLoading, setIsLoading] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  // Filter and search state
  const [filters, setFilters] = useState({
    categories: [],
    brands: [],
    ratings: [],
    skinTypes: [],
    priceRange: [0, 200]
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');

  // Real product feed — fetched from the OffloadHost dashboard's storefront
  // API. Customer ID and API base URL are injected at build time by
  // create-site.sh as Vite env vars (see .env.production). Mirrors the
  // pattern used by other storefronts on the platform (charmsbeautycare etc.).
  const API_BASE    = (import.meta).env.VITE_STOREFRONT_API_URL || 'https://dashboard.offloadhost.com';
  const CUSTOMER_ID = (import.meta).env.VITE_CUSTOMER_ID || '';

  const [mockProducts, setMockProducts] = useState([]);

  // Homepage collection links land here: /product-catalog?filter=new|bestseller|sale
  const collection = searchParams.get('filter');
  // "New" has no date signal in this catalog (bulk import) — treat the highest
  // product ids as the newest arrivals, matching the landing's New Arrivals row.
  const newArrivalIds = React.useMemo(
    () => new Set([...(mockProducts || [])].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0)).slice(0, 8).map((p) => p?.id)),
    [mockProducts]
  );

  useEffect(() => {
    if (!CUSTOMER_ID) return;
    const ctl = new AbortController();
    fetch(`${API_BASE}/storefront/${CUSTOMER_ID}/products`, { signal: ctl.signal })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => {
        const items = (data?.products || []).map((p) => ({
          id: p.id,
          name: p.name,
          brand: 'Bounderlaster',
          price: (p.sale_price_cents ?? p.price_cents) / 100,
          originalPrice: p.sale_price_cents != null ? p.price_cents / 100 : null,
          rating: p.rating_avg ?? 0,
          reviewCount: p.review_count ?? 0,
          image: p.image_url,
          imageAlt: p.name,
          description: p.short_description || p.description || '',
          stock: 99,
          category: p.category,
          isBestseller: !!p.is_featured,
          variants: Array.isArray(p.variants) ? p.variants : [],
          gallery: Array.isArray(p.gallery) ? p.gallery : [],
        }));
        setMockProducts(items);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') console.error('storefront/products fetch failed:', err);
      });
    return () => ctl.abort();
  }, []);


  // Simulate loading and filtering
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [filters, sortBy, searchTerm]);

  // Reset to first page when filters/sort/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortBy, searchTerm, collection]);

  // Filter products based on current filters and search
  const filteredProducts = mockProducts?.filter((product) => {
    // Homepage collection filter (New Arrivals / Best Sellers / Sale)
    if (collection === 'sale' && product?.originalPrice == null) return false;
    if (collection === 'bestseller' && !product?.isBestseller) return false;
    if (collection === 'new' && !newArrivalIds?.has(product?.id)) return false;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      if (!product?.name?.toLowerCase()?.includes(searchLower) &&
      !product?.brand?.toLowerCase()?.includes(searchLower) &&
      !product?.description?.toLowerCase()?.includes(searchLower)) {
        return false;
      }
    }

    // Category filter (simplified - would need proper category mapping)
    if (filters?.categories?.length > 0) {
      // Mock category logic
      return true;
    }

    // Brand filter
    if (filters?.brands?.length > 0) {
      const brandId = product?.brand?.toLowerCase()?.replace(/\s+/g, '-');
      if (!filters?.brands?.includes(brandId)) {
        return false;
      }
    }

    // Price range filter
    if (product?.price < filters?.priceRange?.[0] || product?.price > filters?.priceRange?.[1]) {
      return false;
    }

    // Rating filter
    if (filters?.ratings?.length > 0) {
      const hasMatchingRating = filters?.ratings?.some((rating) => {
        switch (rating) {
          case '4-plus':return product?.rating >= 4;
          case '3-plus':return product?.rating >= 3;
          case '2-plus':return product?.rating >= 2;
          case '1-plus':return product?.rating >= 1;
          default:return true;
        }
      });
      if (!hasMatchingRating) {
        return false;
      }
    }

    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts]?.sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a?.price - b?.price;
      case 'price-high':
        return b?.price - a?.price;
      case 'rating':
        return b?.rating - a?.rating;
      case 'newest':
        return b?.isNew ? 1 : -1;
      case 'bestseller':
        return b?.isBestseller ? 1 : -1;
      case 'name-az':
        return a?.name?.localeCompare(b?.name);
      default:
        return 0;
    }
  });

  // Pagination (client-side over the fetched + filtered + sorted set)
  const totalProducts = sortedProducts?.length;
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedProducts = sortedProducts?.slice(startIndex, startIndex + itemsPerPage);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      categories: [],
      brands: [],
      ratings: [],
      skinTypes: [],
      priceRange: [0, 200]
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  const handleAddToCart = (product) => {
    addItem(product, 1);
    // Show success feedback
  };

  const handleToggleWishlist = (productId, isWishlisted) => {
    // Handle wishlist toggle
    console.log(`Product ${productId} ${isWishlisted ? 'added to' : 'removed from'} wishlist`);
  };

  const handleAuthentication = (authData) => {
    console.log('User authenticated:', authData);
    setShowAuthModal(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <CustomerHeader
        cartItemCount={cartItemCount}
        onCartClick={() => setShowCartSidebar(true)} />


      {/* Main Content */}
      <div className="pt-16 flex-1">
        <div className="w-full">
          <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
            {/* Filter Sidebar */}
            <FilterSidebar
              isOpen={showFilterSidebar}
              onClose={() => setShowFilterSidebar(false)}
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters} />


            {/* Main Content Area */}
            <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8">
              {/* Page Header */}
              <div className="py-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                  <div>
                    <h1 className="text-3xl font-heading font-semibold text-foreground mb-2">
                      Soaps, Oils & Essentials
                    </h1>
                    <p className="text-muted-foreground">
                      Handcrafted soaps, oils & essentials, made in-house
                    </p>
                  </div>

                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowFilterSidebar(true)}
                    iconName="Filter"
                    iconPosition="left"
                    className="lg:hidden">

                    Filters
                  </Button>
                </div>

                {/* Search Bar */}
                <div className="mb-8">
                  <SearchBar onSearch={handleSearch} />
                </div>

                {/* Sort and View Controls */}
                <SortAndViewControls
                  sortBy={sortBy}
                  onSortChange={handleSortChange}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  totalResults={totalProducts}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage} />

              </div>

              {/* Product Grid */}
              <div className="mb-8">
                <ProductGrid
                  products={displayedProducts}
                  viewMode={viewMode}
                  onAddToCart={handleAddToCart}
                  onToggleWishlist={handleToggleWishlist}
                  isLoading={isLoading} />

              </div>

              {/* Pagination */}
              <div className="mb-12">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalProducts}
                  onItemsPerPageChange={handleItemsPerPageChange} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Modals and Sidebars */}
      <AuthenticationModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticate={handleAuthentication}
        initialMode="login" />


      <CartSidebar
        isOpen={showCartSidebar}
        onClose={() => setShowCartSidebar(false)}
        cartItems={[]}
        onUpdateQuantity={() => {}}
        onRemoveItem={() => {}}
        onCheckout={() => {}} />

    </div>);

};

export default ProductCatalog;